import type { GetAggregatedAppointmentDataParams } from '#services/api/insights.ts';
import type { loader as homeLoader } from '../Home.data.ts';

import { endOfDay, format, startOfDay } from 'date-fns';
import { useLoaderData, useSearchParams } from 'react-router';

import {
  isDisplayedProvidersFilter,
  setProviderFilterSearchParams,
} from '#routes/Home/Filters/Provider.utils.ts';
import { apiTimezone, FORMATS } from '#utils/dates.ts';
import { displayedProvidersToken, toProviderIdParam } from '#utils/provider-filter.ts';

const searchParamsWithMultipleValues = [
  { id: 'atId', name: 'appointmentTypeIds' },
  { id: 'aId', name: 'addressIds' },
  { id: 'bcId', name: 'bookingChannelIds' },
  { id: 'bat', name: 'baseAppointmentTypes' },
] as const;

export function useAppointmentWidget() {
  const homeData = useLoaderData<ReturnType<typeof homeLoader>>();

  const [routerSearchParams] = useSearchParams();

  function getCommonRequestParams() {
    return {
      timezone: apiTimezone,
      minStartDate: format(startOfDay(homeData.from), FORMATS.MYSQL_DATETIME),
      maxStartDate: format(endOfDay(homeData.to), FORMATS.MYSQL_DATETIME),
      providerId: toProviderIdParam(homeData.providerIds),
      appointmentTypeId: homeData.appointmentTypeIds,
      addressId: homeData.addressIds,
      bookingChannel: homeData.bookingChannelIds,
      appointmentTypeType: homeData.baseAppointmentTypes,
    } as const satisfies GetAggregatedAppointmentDataParams;
  }

  function getDrilldownRoutePath(appointmentVolumeEligibility = false) {
    const searchParams = new URLSearchParams();

    const view = routerSearchParams.get('view');

    if (view) {
      searchParams.set('view', view);
    } else {
      // Avoid passing date params if they are not set to help in the drilldown
      // filters comparison by not having a mismatch of the keys with the same
      // internal values.
      const dp = routerSearchParams.get('dp');
      if (dp) {
        searchParams.set('dp', dp);
      } else {
        if (routerSearchParams.get('from')) {
          searchParams.set('from', homeData.fromDateFormat);
        }
        if (routerSearchParams.get('to')) {
          searchParams.set('to', homeData.toDateFormat);
        }
      }

      setProviderFilterSearchParams(
        searchParams,
        isDisplayedProvidersFilter(routerSearchParams)
          ? displayedProvidersToken
          : homeData.providerIds
      );

      for (const searchParamWithMultipleValues of searchParamsWithMultipleValues) {
        for (const value of homeData[searchParamWithMultipleValues.name]) {
          searchParams.append(searchParamWithMultipleValues.id, String(value));
        }
      }
    }

    if (appointmentVolumeEligibility) {
      searchParams.set('appointmentVolumeEligibility', '1');
    }

    searchParams.set('tab', 'scheduling');
    return `/appointments/drilldown?${searchParams.toString()}` as const;
  }

  return {
    getCommonRequestParams,
    getDrilldownRoutePath,
    homeLoaderData: homeData,
  };
}
